import {FC, useEffect, useRef, useState} from 'react';
import {Flex, Text} from '@mantine/core';
import clsx from 'clsx';
import {useDispatch} from 'react-redux';

import {NotificationType, TNotification} from '~/services/hot-api/notifications';
import Plate from '~/common/display/Plate';
import {PlateType} from '~/common/display/Plate/Plate';
import Icon, {Icons} from '~/common/Icon';
import {formatDate} from '~/utils/dates';

import {ActionsMap, NotificationToPlateMap} from '../../constants';
import s from './styles.module.scss';

interface NotificationItemProps {
  notification: TNotification;
  onOpen: () => void;
  onDelete: () => void;
  onAction: () => void;
  isRead: boolean;
}

const OFFSET_BREAKPOINT = 5;

const NotificationItem: FC<NotificationItemProps> = ({
  notification,
  onOpen,
  onDelete,
  isRead,
  onAction,
}) => {
  const dispatch = useDispatch();
  const contentRef = useRef<HTMLDivElement>(null);

  const [isClamped, setClamped] = useState(false);
  const notificationType = notification.type || NotificationType.info;
  const plateType = isRead
    ? PlateType.transparent
    : NotificationToPlateMap[notificationType].plateType;

  useEffect(() => {
    const textNode = contentRef.current;

    if (!textNode) {
      return;
    }

    if (Math.abs(textNode.scrollHeight - textNode.clientHeight) > OFFSET_BREAKPOINT) {
      setClamped(true);
    }
  }, []);

  const timeAdded = formatDate(notification.added, 'HH:mm');

  const handleActionClick = () => {
    onAction();

    if (isRead) {
      return;
    }
    dispatch.profile.setNotificationAsRead(notification.id);
  };

  const handleReadMoreClick = () => {
    if (!isRead) {
      dispatch.profile.setNotificationAsRead(notification.id);
    }

    onOpen();
  };

  return (
    <div className={s.notification}>
      <Icon name={Icons.crossSmall} className={s['notification__close-icon']} onClick={onDelete} />
      <Plate type={plateType}>
        <div className={s.notification__header} onClick={handleReadMoreClick}>
          <Icon
            name={Icons.infoCircle}
            className={clsx(s.notification__icon, s[`notification__icon--${notificationType}`])}
          />
          <div className={s.notification__title}>
            <div>{notification.title}</div>
            <div className={s.notifcation__date}>{timeAdded}</div>
          </div>
        </div>
        {notification.content && (
          <Text ref={contentRef} lineClamp={3} className={s.notification__content} mt={22}>
            {notification.content}
          </Text>
        )}
        <div className={s.notification__controls}>
          {isClamped && (
            <div className={s['notification__read-more']} onClick={handleReadMoreClick}>
              Read More
            </div>
          )}
        </div>
        {notification.actions.length > 0 && (
          <Flex mt={20} align="end" justify="space-between">
            <div className={s.notification__actions}>
              {notification.actions.map((action) => {
                const Component = ActionsMap[action.type];

                if (!Component) {
                  console.warn(`No component for action type: ${action.type}`);
                  return null;
                }

                return (
                  <Component
                    key={action.type}
                    // TODO: make type on of Pick<NotificationPayloadMap, T>
                    payload={action.payload as any}
                    onAction={handleActionClick}
                    variant="light"
                  />
                );
              })}
            </div>
          </Flex>
        )}
      </Plate>
    </div>
  );
};

export default NotificationItem;
